import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  fetchDataExportHistory,
  fetchDataExportJobStatus,
  fetchDataExportRegistry,
  triggerDataExport,
  cancelDataExportJob,
} from "../api/rest/dataExport";
import type {
  DataExportApiError,
  DataExportDownloadRequest,
  DataExportHistoryItem,
  DataExportJobStatus,
  DataExportRegistryItem,
} from "../types/dataExport";

/** Fetch all available export types from the registry. */
export const useDataExportRegistry = () =>
  useQuery<DataExportRegistryItem[]>(
    ["dataExportRegistry"],
    fetchDataExportRegistry,
    { staleTime: Infinity } // static — never changes without a deploy
  );

/** Fetch export history for the store. */
export const useDataExportHistory = () =>
  useQuery<DataExportHistoryItem[]>(
    ["dataExportHistory"],
    fetchDataExportHistory,
    { refetchOnWindowFocus: false }
  );

/** Poll a single async job until it reaches a terminal state. */
export const useDataExportJobPoller = (
  jobId: number | null,
  onComplete: (result: DataExportJobStatus) => void
) => {
  const [polling, setPolling] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCompletedRef = useRef(false);
  const queryClient = useQueryClient();

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPolling(false);
  }, []);

  const startPolling = useCallback(
    (id: number) => {
      // Ensure there is only one active interval for this hook instance.
      stopPolling();
      isCompletedRef.current = false;
      setPolling(true);
      intervalRef.current = setInterval(async () => {
        try {
          const result = await fetchDataExportJobStatus(id);
          if (
            (result.status === "ready" ||
              result.status === "error" ||
              result.status === "cancelled") &&
            !isCompletedRef.current
          ) {
            isCompletedRef.current = true;
            stopPolling();
            // Refresh history so the new item shows immediately
            queryClient.invalidateQueries(["dataExportHistory"]);
            onComplete(result);
          }
        } catch (err) {
          const apiError = err as DataExportApiError;
          if (isCompletedRef.current) return;
          isCompletedRef.current = true;
          stopPolling();
          onComplete({
            jobId: id,
            status: "error",
            errorMessage:
              apiError?.status === 401
                ? "Unauthorized. Please sign in again."
                : "Export job not found or invalid.",
          });

          if (apiError?.status === 401 && typeof window !== "undefined") {
            window.location.hash = "#/";
          }
        }
      }, 3000); // poll every 3 seconds
    },
    [stopPolling, onComplete, queryClient]
  );

  // Clean up on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  return { polling, startPolling, stopPolling };
};

/**
 * Trigger a data export.
 * - sync: downloads file directly via a temporary anchor element
 * - async: enqueues job and starts polling
 */
export const useTriggerDataExport = (
  onAsyncQueued: (jobId: number, exportLabel: string) => void
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trigger = useCallback(
    async (
      request: DataExportDownloadRequest,
      exportLabel: string
    ): Promise<{ ok: boolean; error?: string; fatal?: boolean }> => {
      const downloadBlob = (blob: Blob, fileName: string) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Delay revoke so browser can finish reading the Blob URL.
        setTimeout(() => URL.revokeObjectURL(url), 0);
      };

      setLoading(true);
      setError(null);
      try {
        const result = await triggerDataExport(request);

        if (result.type === "queued") {
          onAsyncQueued(result.jobId, exportLabel);
        } else {
          // Sync: response is a CSV blob — download with formatted name
          const now = new Date();
          const pad = (n: number) => String(n).padStart(2, "0");
          const datetime = `${now.getFullYear()}-${pad(
            now.getMonth() + 1
          )}-${pad(now.getDate())} ${pad(now.getHours())}-${pad(
            now.getMinutes()
          )}-${pad(now.getSeconds())}`;
          const safeName = exportLabel.replace(/[/\\:*?"<>|]/g, "").trim();
          downloadBlob(result.blob, `${safeName} ${datetime}.csv`);
        }
        return { ok: true };
      } catch (err) {
        const apiError = err as DataExportApiError;
        if (apiError?.status === 401) {
          const error = "Unauthorized. Please sign in again.";
          setError(error);
          if (typeof window !== "undefined") {
            window.location.hash = "#/";
          }
          return { ok: false, error, fatal: true };
        }

        if (apiError?.status === 404) {
          const error =
            apiError?.error ||
            "Export type not found. Refresh exports and re-select a type.";
          setError(error);
          return { ok: false, error };
        }

        if (apiError?.status === 409) {
          const error =
            apiError?.error ||
            "An export of this type is already in progress. Please wait for it to complete.";
          setError(error);
          return { ok: false, error };
        }

        const error =
          apiError?.error ||
          apiError?.errorMessage ||
          "Export failed. Please try again.";
        setError(error);
        return { ok: false, error };
      } finally {
        setLoading(false);
      }
    },
    [onAsyncQueued]
  );

  return {
    trigger,
    loading,
    error,
    clearError: () => setError(null),
    setError,
  };
};

/**
 * Cancel an in-progress async export job.
 * Tracks per-job cancelling state and refreshes history on success.
 */
export const useCancelDataExportJob = () => {
  const [cancellingIds, setCancellingIds] = useState<Set<number>>(new Set());
  const queryClient = useQueryClient();

  const cancel = useCallback(
    async (jobId: number): Promise<boolean> => {
      setCancellingIds((prev) => new Set(prev).add(jobId));
      try {
        await cancelDataExportJob(jobId);
        queryClient.invalidateQueries(["dataExportHistory"]);
        return true;
      } catch (err) {
        const apiError = err as DataExportApiError;
        toast.error(apiError?.error || "Failed to cancel export job.");
        return false;
      } finally {
        setCancellingIds((prev) => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      }
    },
    [queryClient]
  );

  return { cancel, cancellingIds };
};
